export const DEMO_CLIENT_IDS = [
  "client_heatpump_001",
  "client_pool_001",
  "client_solar_001",
] as const;

export function isDemoClientId(clientId: string | null | undefined): boolean {
  if (!clientId) return false;
  return (DEMO_CLIENT_IDS as readonly string[]).includes(clientId);
}

/**
 * Demo dashboards have no session - `?client_id=client_*` IS the credential.
 * Every place that rewrites the dashboard URL can therefore log a demo visitor
 * out by dropping one query param. This cookie mirrors the param so the URL is
 * cosmetic: losing it resets a form switcher, it no longer bounces to /login.
 *
 * Not httpOnly - the value is a public demo id, no secret, and the client shell
 * reads it as a fallback. Only ever consulted when there is no Supabase session.
 */
export const DEMO_CLIENT_COOKIE = "strelve_demo_client";
export const DEMO_CLIENT_COOKIE_MAX_AGE = 60 * 60; // 1h

export function readDemoClientCookie(
  cookies: { get: (name: string) => { value: string } | undefined }
): string | null {
  const value = cookies.get(DEMO_CLIENT_COOKIE)?.value ?? null;
  return isDemoClientId(value) ? value : null;
}

export function readDemoClientCookieFromDocument(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${DEMO_CLIENT_COOKIE}=([^;]*)`)
  );
  const value = match ? decodeURIComponent(match[1]) : null;
  return isDemoClientId(value) ? value : null;
}

// Fixed read-only demo API keys. Safe to expose — they only serve mock data.
const DEMO_API_KEYS: Record<string, string> = {
  "sk_demo_solar001": "client_solar_001",
  "sk_demo_pool001": "client_pool_001",
  "sk_demo_heatpump001": "client_heatpump_001",
};

export function getDemoClientIdFromApiKey(rawKey: string): string | null {
  return DEMO_API_KEYS[rawKey] ?? null;
}

export function getDemoApiKeyForClient(clientId: string): string | null {
  const entry = Object.entries(DEMO_API_KEYS).find(([, cid]) => cid === clientId);
  return entry?.[0] ?? null;
}
